import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import type { AxiosError } from 'axios';
import {
	fetchNewReceivedPackagesCount,
	fetchNewReceivedServiceRequestsCount,
} from '@meddihub/mzcr-ui';
import type { AxiosInstance } from 'axios';
import type { Organization, Practitioner } from '@meddihub/mzcr-ui';
import { getMzcrClient } from './mzcrClient';

/** Za koho se služby MZČR volají – pracoviště s IČO a přihlášený lékař (z user-api). */
export interface MzcrContext {
	organization: Organization;
	practitioner: Practitioner;
}

export type RidLookupResult =
	| { found: true; rid: string; method: 'insurance' | 'birthDate' | 'stored' }
	| { found: false; reason: 'MISSING_INPUT_DATA' | 'NOT_FOUND' };

interface MzcrQueryArgs {
	url: string;
	method?: 'GET' | 'POST';
}

interface MzcrQueryError {
	status?: number;
	message: string;
}

const toMzcrQueryError = (e: unknown): MzcrQueryError => {
	const error = e as AxiosError;
	return { status: error.response?.status, message: error.message };
};

const mzcrBaseQuery: BaseQueryFn<
	MzcrQueryArgs,
	unknown,
	MzcrQueryError
> = async ({ url, method = 'GET' }) => {
	try {
		const response = await getMzcrClient().request({ url, method });
		return { data: response.data };
	} catch (e) {
		return { error: toMzcrQueryError(e) };
	}
};

const countQueryFn =
	(fetchCount: (mzcrClient: AxiosInstance) => Promise<number>) => async () => {
		try {
			return { data: await fetchCount(getMzcrClient()) };
		} catch (e) {
			return { error: toMzcrQueryError(e) };
		}
	};

/**
 * Calls of user-api `/mzcr` made by the host app itself (the MZČR services of mzcr-ui call it on
 * their own). Cached, so the context is loaded once and not by every tab of the patient card.
 */
export const mzcrApi = createApi({
	reducerPath: 'mzcrApi',
	baseQuery: mzcrBaseQuery,
	tagTypes: ['PatientRid'],
	endpoints: build => ({
		getMzcrContext: build.query<MzcrContext, void>({
			query: () => ({ url: 'context' }),
			keepUnusedDataFor: 300,
		}),
		/** RID already stored in the patient's profile - `null` when not paired yet. */
		getPatientRid: build.query<{ rid: string | null }, string>({
			query: patientId => ({ url: `patients/${patientId}/rid` }),
			providesTags: (_result, _error, patientId) => [
				{ type: 'PatientRid', id: patientId },
			],
		}),
		/** Looks the patient up in the MZČR registry and stores the RID in the profile. */
		lookupPatientRid: build.mutation<RidLookupResult, string>({
			query: patientId => ({
				url: `patients/${patientId}/rid`,
				method: 'POST',
			}),
			invalidatesTags: (_result, _error, patientId) => [
				{ type: 'PatientRid', id: patientId },
			],
		}),
		getNewReceivedPackagesCount: build.query<number, void>({
			queryFn: countQueryFn(fetchNewReceivedPackagesCount),
		}),
		getNewReceivedServiceRequestsCount: build.query<number, void>({
			queryFn: countQueryFn(fetchNewReceivedServiceRequestsCount),
		}),
	}),
});

export const {
	useGetMzcrContextQuery,
	useGetPatientRidQuery,
	useLazyGetPatientRidQuery,
	useLookupPatientRidMutation,
	useLazyGetNewReceivedPackagesCountQuery,
	useLazyGetNewReceivedServiceRequestsCountQuery,
} = mzcrApi;
